import * as React from 'react'
import { ArrowDown, ArrowUp, ChevronRight, GripVertical, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  VENUE_MARKETING_BODY_SECTIONS,
  venueMarketingBodySectionSchema,
  type VenueMarketingBodySection,
} from '@lib/dto/venue-marketing.dto'
import type { Json } from '@lib/helpers/json'
import { getList, pathKey, setAt, type Draft, type DraftProblems } from './draft'
import { MARKETING_SECTIONS, type SectionSpec } from './sections'
import { SectionForm } from './SectionForm'

/**
 * Whether anything in here would actually render.
 *
 * Recursive, because stopping at "is an object" counted an *empty* nested group
 * as content — `micro.tasting`, and every `kind: 'group'` field, which a newly
 * added section starts as `{}`.
 */
export function hasContent(value: Json | undefined): boolean {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value !== ''
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.some(hasContent)
  return Object.values(value).some(hasContent)
}

/**
 * The order the page will actually render its body in.
 *
 * Listed sections first, then everything else in default order — the site's own
 * rule, applied here so the editor shows what will happen rather than what was
 * typed. Entries naming no section are dropped rather than rendered.
 */
export function effectiveOrder(draft: Draft): VenueMarketingBodySection[] {
  const listed = getList(draft, ['sectionOrder']).flatMap((entry) => {
    const parsed = venueMarketingBodySectionSchema.safeParse(entry)
    return parsed.success ? [parsed.data] : []
  })
  const seen = new Set(listed)
  return [...listed, ...VENUE_MARKETING_BODY_SECTIONS.filter((key) => !seen.has(key))]
}

/** A section's place on the page, which decides whether it can be moved. */
type Placement = 'top' | 'body' | 'bottom'

const TOP: readonly string[] = ['hero', 'intro', 'guide']
const BOTTOM: readonly string[] = ['contact', 'brand', 'footer']

interface Row {
  spec: SectionSpec
  placement: Placement
  /** Position within the orderable run, or -1 for a fixed section. */
  bodyIndex: number
}

/**
 * Every section this page has, in the order the page renders them.
 *
 * Fixed top, orderable body, fixed bottom — the page's real shape, which is why
 * the ordering and the forms are one list rather than two. Editing a section and
 * deciding where it goes were separate panels to begin with, and reading the
 * order in one place while editing in another made the relationship between them
 * something you had to hold in your head.
 */
export function rowsFor(draft: Draft): Row[] {
  const byKey = new Map<string, SectionSpec>(MARKETING_SECTIONS.map((spec) => [spec.key, spec]))
  const present = (spec: SectionSpec): boolean => !spec.optional || draft[spec.key] !== undefined

  const rows: Row[] = []
  for (const key of TOP) {
    const spec = byKey.get(key)
    if (spec && present(spec)) rows.push({ spec, placement: 'top', bodyIndex: -1 })
  }
  let index = 0
  for (const key of effectiveOrder(draft)) {
    const spec = byKey.get(key)
    if (spec && present(spec)) {
      rows.push({ spec, placement: 'body', bodyIndex: index })
      index += 1
    }
  }
  for (const key of BOTTOM) {
    const spec = byKey.get(key)
    if (spec && present(spec)) rows.push({ spec, placement: 'bottom', bodyIndex: -1 })
  }
  return rows
}

/**
 * The editor's spine: order and content in one list.
 *
 * Dragging is the quick way to reorder and the arrows are the real one — a list
 * that can only be reordered by dragging cannot be reordered from a keyboard,
 * and this is a settings form. Only the body rows carry either: the top of a
 * page and its footer are fixed, and offering a grip that does nothing would be
 * worse than offering none.
 */
export function SectionList({
  draft,
  onChange,
  problems,
  venueId,
  open,
  onToggle,
  canEdit,
}: {
  draft: Draft
  onChange: (next: Draft) => void
  problems: DraftProblems
  venueId: string
  open: ReadonlySet<string>
  onToggle: (key: string) => void
  /** Edit adds reordering, removing and changing sections; without it each still opens, to be read. */
  canEdit: boolean
}): React.ReactElement {
  const rows = rowsFor(draft)
  const body = rows.filter((row) => row.placement === 'body')
  const [dragging, setDragging] = React.useState<number | null>(null)

  /**
   * Move a body section, writing the **whole** order explicitly.
   *
   * A partial list is valid — the site appends what is missing — but storing one
   * would mean the page silently rearranging the day a section is added to the
   * contract.
   */
  function move(from: number, to: number): void {
    if (to < 0 || to >= body.length || from === to) return
    // Parsed rather than asserted: a row's key is any section name, and only a
    // body section can appear in the order.
    const moving = venueMarketingBodySectionSchema.safeParse(body[from]?.spec.key)
    const before = venueMarketingBodySectionSchema.safeParse(body[to]?.spec.key)
    if (!moving.success || !before.success) return
    const next = effectiveOrder(draft).filter((key) => key !== moving.data)
    const at = next.indexOf(before.data)
    next.splice(from < to ? at + 1 : at, 0, moving.data)
    onChange(setAt(draft, ['sectionOrder'], [...next]))
  }

  return (
    <div className="divide-y divide-line rounded-md border border-line">
      {rows.map((row) => {
        const { spec, placement, bodyIndex } = row
        const isOpen = open.has(spec.key)
        const trouble = (problems.bySubtree.get(pathKey([spec.key])) ?? []).length
        const movable = canEdit && placement === 'body'
        const filled = hasContent(draft[spec.key])
        return (
          <div
            key={spec.key}
            {...(movable
              ? {
                  draggable: !isOpen,
                  onDragStart: (event: React.DragEvent) => {
                    // Firefox will not begin a drag unless dragstart sets data.
                    event.dataTransfer.setData('text/plain', spec.key)
                    event.dataTransfer.effectAllowed = 'move'
                    setDragging(bodyIndex)
                  },
                  onDragEnd: () => {
                    setDragging(null)
                  },
                  onDragOver: (event: React.DragEvent) => {
                    event.preventDefault()
                    if (dragging === null || dragging === bodyIndex) return
                    move(dragging, bodyIndex)
                    setDragging(bodyIndex)
                  },
                  onDrop: (event: React.DragEvent) => {
                    event.preventDefault()
                    setDragging(null)
                  },
                }
              : {})}
            className={dragging === bodyIndex && movable ? 'opacity-40' : ''}
          >
            <div className="flex items-center gap-1 px-2 py-2">
              {movable ? (
                // Dimmed while the section is open, because an open row is not
                // draggable — hauling a form the height of the screen around is
                // worse than reaching for the arrows, which still work. A grip
                // that looks the same either way would be claiming otherwise.
                <GripVertical
                  className={`size-4 shrink-0 text-ink-muted ${isOpen ? 'opacity-30' : 'cursor-grab'}`}
                  aria-hidden
                />
              ) : (
                <span className="size-4 shrink-0" aria-hidden />
              )}
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                aria-expanded={isOpen}
                onClick={() => {
                  onToggle(spec.key)
                }}
              >
                <ChevronRight
                  className={`size-4 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink">
                    {spec.label}
                    {!filled ? (
                      <span className="ml-2 text-xs font-normal text-ink-muted">
                        empty — skipped
                      </span>
                    ) : null}
                  </span>
                  <span className="block truncate text-xs text-ink-muted">
                    {spec.description}
                  </span>
                </span>
              </button>

              {trouble > 0 ? (
                <Badge tone="warning">
                  {trouble} {trouble === 1 ? 'problem' : 'problems'}
                </Badge>
              ) : null}

              {movable ? (
                <span className="flex shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${spec.label} up`}
                    disabled={bodyIndex === 0}
                    onClick={() => {
                      move(bodyIndex, bodyIndex - 1)
                    }}
                  >
                    <ArrowUp className="size-3.5" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${spec.label} down`}
                    disabled={bodyIndex === body.length - 1}
                    onClick={() => {
                      move(bodyIndex, bodyIndex + 1)
                    }}
                  >
                    <ArrowDown className="size-3.5" aria-hidden />
                  </Button>
                </span>
              ) : null}

              {canEdit && spec.optional ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove the ${spec.label} section`}
                  onClick={() => {
                    onChange(setAt(draft, [spec.key], undefined))
                  }}
                >
                  <Trash2 className="size-3.5 text-danger-ink" aria-hidden />
                </Button>
              ) : null}
            </div>

            {isOpen ? (
              <div className="space-y-4 border-t border-line px-3 py-4">
                {/* Read-only is the form with every control in it disabled, so a
                    section reads exactly as it would be edited. */}
                <fieldset disabled={!canEdit} className="m-0 min-w-0 border-0 p-0">
                  <SectionForm control={{ draft, onChange, problems, venueId }} spec={spec} />
                </fieldset>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
